import { useEffect, useState } from 'react';
import { measurementStepKeys, type MeasurementStepKey } from '@st/contract';
import {
  useSaveMeasurementGuide,
  useTrainerMeasurementGuide,
  useUploadMeasurementVideo,
} from '../../api/queries';
import { PageHeader } from '../../components/PageHeader';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import { Badge, Button, SkeletonList, Textarea, useToast } from '../../ui';

/** Naziv točke — isti niz koji klijent vidi uz polje u aplikaciji. */
const LABEL: Record<MeasurementStepKey, string> = {
  weight: 'progress.weight',
  waist: 'progress.waist',
  chest: 'progress.chest',
  hips: 'progress.hips',
  arm: 'progress.arm',
  thigh: 'progress.thigh',
  body_fat: 'progress.bodyFat',
};

/** 48 MB — `/v1/files` čita tijelo u memoriju, a Worker ima 128 MB. */
const MAX_BYTES = 48 * 1024 * 1024;

interface Korak {
  key: MeasurementStepKey;
  hr: string;
  en: string;
  videoKey: string | null;
  videoUrl: string | null;
}

/**
 * Uputa „kako se mjeri" — tekst i snimka po TOČKI mjerenja.
 *
 * Zašto po točki: klijent uputu traži u trenutku kad već drži metar oko
 * struka. Jedna zajednička snimka za svih pet opsega znači premotavanje do
 * mjesta koje ga zanima, pa je u aplikaciji uputa uz svako polje.
 *
 * Uređuju je trener i admin. Postavlja se jednom, ali se mora moći zamijeniti
 * kad se snimi bolja — zato je zamjena obična radnja, a ne skriveni popravak.
 *
 * Točka bez snimke nije greška: aplikacija tada ponudi opću snimku cijelog
 * mjerenja, a ako ni nje nema, prikaže samo tekst.
 */
export function TrainerMeasurementGuide() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const query = useTrainerMeasurementGuide();
  const save = useSaveMeasurementGuide();
  const upload = useUploadMeasurementVideo();

  const [koraci, setKoraci] = useState<Korak[] | null>(null);
  const [saljem, setSaljem] = useState<MeasurementStepKey | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  /*
    Popis točaka je ZATVOREN i dolazi iz ugovora, a ne iz spremljenog zapisa:
    točka koju trener nije nikad dotaknuo mora se svejedno vidjeti, inače
    uputa za bedro ne postoji dok netko ne primijeti da je nema.
  */
  useEffect(() => {
    if (!query.data) return;
    setKoraci(
      measurementStepKeys.map((key) => {
        const spremljen = query.data.steps.find((s) => s.key === key);
        return {
          key,
          hr: spremljen?.hr ?? '',
          en: spremljen?.en ?? '',
          videoKey: spremljen?.videoKey ?? null,
          videoUrl: spremljen?.videoUrl ?? null,
        };
      }),
    );
  }, [query.data]);

  const izmijeni = (key: MeasurementStepKey, patch: Partial<Korak>) =>
    setKoraci((prev) => prev?.map((k) => (k.key === key ? { ...k, ...patch } : k)) ?? prev);

  async function odaberiSnimku(key: MeasurementStepKey, file: File): Promise<void> {
    setGreska(null);

    if (file.size > MAX_BYTES) {
      setGreska(t('measureGuide.tooLarge', { max: Math.round(MAX_BYTES / (1024 * 1024)) }));
      return;
    }
    if (file.type !== 'video/mp4' && file.type !== 'video/webm') {
      setGreska(t('measureGuide.wrongType'));
      return;
    }

    setSaljem(key);
    try {
      const { key: videoKey } = await upload.mutateAsync({ stepKey: key, file });
      /*
        Prijenos i spremanje su dva koraka namjerno: prekinut prijenos ne smije
        ostaviti uputu koja pokazuje na objekt kojeg nema. Zato se nova snimka
        vidi odmah lokalno, a u bazu ide tek pritiskom na „Spremi".
      */
      izmijeni(key, { videoKey, videoUrl: URL.createObjectURL(file) });
      toast(t('measureGuide.uploaded'), 'success');
    } catch (err) {
      setGreska(toMutationMessage(t, err));
    } finally {
      setSaljem(null);
    }
  }

  async function spremi(): Promise<void> {
    if (!koraci) return;
    setGreska(null);
    try {
      await save.mutateAsync({
        steps: koraci.map((k) => ({
          key: k.key,
          hr: k.hr.trim(),
          en: k.en.trim(),
          videoKey: k.videoKey,
        })),
      });
      toast(t('measureGuide.saved'), 'success');
    } catch (err) {
      setGreska(toMutationMessage(t, err));
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={t('shell.trainer')}
        title={t('measureGuide.title')}
        action={
          <Button size="sm" loading={save.isPending} onClick={() => void spremi()}>
            {t('common.save')}
          </Button>
        }
      />

      <p className="mb-5 max-w-prose text-[13.5px] leading-relaxed text-ink-muted">
        {t('measureGuide.help')}
      </p>

      {greska && (
        <p role="alert" className="mb-4 text-[13px] text-danger">
          {greska}
        </p>
      )}

      <QueryBoundary query={query} skeleton={<SkeletonList rows={4} />}>
        {() => (
          <div className="flex flex-col gap-4">
            {(koraci ?? []).map((korak) => (
              <section
                key={korak.key}
                className="flex flex-col gap-3 rounded-xl border border-line-subtle bg-surface p-5 shadow-card"
              >
                <div className="flex items-center justify-between gap-3">
                  <h2 className="font-display text-[19px] font-semibold text-ink">
                    {t(LABEL[korak.key])}
                  </h2>
                  <Badge tone={korak.videoKey ? 'success' : 'neutral'}>
                    {korak.videoKey ? t('measureGuide.hasVideo') : t('measureGuide.noVideo')}
                  </Badge>
                </div>

                <Textarea
                  id={`guide-hr-${korak.key}`}
                  label={t('measureGuide.textHr')}
                  rows={2}
                  maxLength={600}
                  value={korak.hr}
                  onChange={(e) => izmijeni(korak.key, { hr: e.target.value })}
                />
                <Textarea
                  id={`guide-en-${korak.key}`}
                  label={t('measureGuide.textEn')}
                  rows={2}
                  maxLength={600}
                  value={korak.en}
                  onChange={(e) => izmijeni(korak.key, { en: e.target.value })}
                />

                {korak.videoUrl && (
                  /* Pregled: snimka se provjeri prije nego je klijent vidi. */
                  <video
                    src={korak.videoUrl}
                    controls
                    preload="metadata"
                    className="w-full max-w-md rounded-md bg-ink"
                    aria-label={t('measureGuide.preview', { point: t(LABEL[korak.key]) })}
                  />
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <label
                    htmlFor={`guide-video-${korak.key}`}
                    className="cursor-pointer rounded-pill border border-line-control bg-surface px-4 py-2 font-body text-[13px] font-medium text-ink hover:bg-bg-alt"
                  >
                    {korak.videoKey ? t('measureGuide.replace') : t('measureGuide.upload')}
                  </label>
                  <input
                    id={`guide-video-${korak.key}`}
                    type="file"
                    accept="video/mp4,video/webm"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void odaberiSnimku(korak.key, file);
                      e.target.value = '';
                    }}
                  />
                  {korak.videoKey && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => izmijeni(korak.key, { videoKey: null, videoUrl: null })}
                    >
                      {t('measureGuide.remove')}
                    </Button>
                  )}
                  {saljem === korak.key && (
                    <span aria-live="polite" className="text-[12.5px] text-ink-muted">
                      {t('measureGuide.uploading')}
                    </span>
                  )}
                </div>
              </section>
            ))}

            <div>
              <Button loading={save.isPending} onClick={() => void spremi()}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
